/**
 * TanStack Query hooks for the patient profile builder — admin creates a
 * full patient profile directly (bypassing self-signup), optionally with
 * products pre-assigned to a suggested cart (customer/migration mode).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from './client';

export interface AddressIn {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country?: string;
}

export interface ProductLineIn {
  product_id: string;
  // Always 1: the patient's cart holds one unit per product.
  quantity: 1;
}

export interface CreatePatientProfileIn {
  first_name: string;
  last_name: string;
  email: string;
  // Required for every profile, not just customer/migration mode.
  phone: string;
  // Required only when product_lines is given (something to ship) — the
  // New Hire funnel has neither.
  address?: AddressIn;
  date_of_birth?: string; // YYYY-MM-DD
  sex?: 'male' | 'female' | 'other' | 'unknown';
  reason_code: string;
  reason?: string;
  product_lines?: ProductLineIn[];
  cart_note?: string;
}

export interface PatientProfileOut {
  id: string;
  email: string | null;
  display_name: string | null;
  emr_patient_id: string | null;
  emr_provider: string | null;
  // True when this submit finished an earlier, partly failed create for the
  // same profile instead of creating a new one.
  resumed: boolean;
  // Steps that still failed (Healthie address, suggested products).
  // Submitting the same details again retries them.
  warnings: string[];
}

const RECENT_PROFILES_KEY = ['admin', 'patient-profiles', 'recent'];

export function useCreatePatientProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePatientProfileIn) =>
      apiFetch<PatientProfileOut>('/admin/patient-profiles', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RECENT_PROFILES_KEY });
    },
  });
}

export function useSendProfileInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patientId: string) =>
      apiFetch<{ sent: boolean }>(
        `/admin/patient-profiles/${patientId}/send-invite`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RECENT_PROFILES_KEY });
    },
  });
}

export interface RecentProfile {
  patient_id: string;
  email: string | null;
  display_name: string | null;
  mode: 'customer' | 'new_hire' | null;
  reason_code: string | null;
  created_at: string;
  invited_at: string | null;
}

export function useRecentPatientProfiles() {
  return useQuery({
    queryKey: RECENT_PROFILES_KEY,
    queryFn: () =>
      apiFetch<{ profiles: RecentProfile[] }>('/admin/patient-profiles'),
    staleTime: 30_000,
  });
}
